import React, { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { getValue } from '../../utils/tools';
import { DEFAULT_ADVANCE } from './WalkthroughAdvanceControl';

// Walkthrough learner renderer — Spotlight playback (BL-147).
// Dims the page and highlights the current step's hotspot in place, with an
// auto-positioned instruction tooltip. Shares theming, the top nav, and the
// advance triggers (BL-153) with the Default player. When a step has no hotspot
// the whole page dims and the tooltip anchors bottom-right so the learner can
// still read it and press Next.

const richHtml = (v) => DOMPurify.sanitize(String(v || '').replace(/\n/g, '<br>'));
const TIPW = 300;
const TIPH = 184;

const WalkthroughSpotlight = ({ steps, index, configColors, onNext, onPrev, onGoto, showNav = true, brandName, companySlug, learner, title }) => {
  const list = Array.isArray(steps) ? steps : [];
  const current = list[index] || {};
  const isFirst = index <= 0;
  const isLast = index >= list.length - 1;

  const stageRef = useRef(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const measure = () => {
      const el = stageRef.current;
      if (el) setDims({ w: el.clientWidth, h: el.clientHeight });
    };
    measure();
    const timers = [120, 400, 900, 1600].map((ms) => setTimeout(measure, ms));
    window.addEventListener('resize', measure);
    return () => { timers.forEach(clearTimeout); window.removeEventListener('resize', measure); };
  }, [index, current.backgroundUrl]);

  // Theming (shared with the Default player).
  const navBg = getValue(configColors, 'tn', 'tnBGColor') || '#FFFFFF';
  const navText = getValue(configColors, 'tn', 'tnTextColor') || '#16232E';
  const navLink = getValue(configColors, 'tn', 'tnLinkColor') || '#0E7C86';
  const logo = getValue(configColors, 'tn', 'tnLogo');
  const primary = (configColors && configColors.primary) || '#0E7C86';
  const buttonColor = getValue(configColors, 'wp', 'wpButtonColor') || primary;
  const ring = getValue(configColors, 'wp', 'wpHighlightColor') || navLink;
  const stepColor = getValue(configColors, 'wp', 'wpStepColor') || navLink;
  const hlColor = getValue(configColors, 'mca', 'mcaStepHeadlineColor') || '#16232E';
  const hlFont = getValue(configColors, 'mca', 'mcaStepHeadlineFont');
  const descColor = getValue(configColors, 'mca', 'mcaStepDescriptionColor') || '#5A6B7E';
  const descFont = getValue(configColors, 'mca', 'mcaStepDescriptionFont');

  // Advance triggers (BL-153).
  const adv = { ...DEFAULT_ADVANCE, ...(current.advance || {}) };
  const hotspotAdvances = !!adv.hotspot && !adv.auto;
  const showNext = isLast || (!adv.auto && (adv.nextButton || !adv.hotspot));

  // Completion — finishing the last step shows a completion modal with Restart
  // (replay in place) and Exit (back to the learner Hub).
  const [done, setDone] = useState(false);
  const handleForward = () => { if (onNext) onNext(); if (isLast) setDone(true); };
  const restart = () => { setDone(false); if (onGoto) onGoto(list[0], 0); };
  const exitToHub = () => { window.location.href = companySlug ? `/${companySlug}` : '/'; };

  useEffect(() => {
    if (!adv.auto || done) return undefined;
    const t = setTimeout(() => { handleForward(); }, Math.max(1, adv.autoDelay || 3) * 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, adv.auto, adv.autoDelay, done]);

  // Target geometry — the hotspot is stored in the 1024-wide stage space.
  const layers = current.layers || [];
  const hotspot = layers.find((l) => l && l.type === 'hotspot');
  const scale = dims.w ? dims.w / 1024 : 0;
  let spot = null;
  if (hotspot && scale) {
    spot = {
      left: (hotspot.x || 0) * scale,
      top: (hotspot.y || 0) * scale,
      w: (hotspot.width || 120) * scale,
      h: (hotspot.height || 40) * scale
    };
  }

  // Tooltip placement: below the target, flipping above near the bottom edge,
  // clamped horizontally; bottom-right when there's no hotspot to point at.
  let tip;
  if (spot) {
    let top = spot.top + spot.h + 12;
    if (top + TIPH > dims.h) top = Math.max(8, spot.top - TIPH - 12);
    const left = Math.min(Math.max(8, spot.left), Math.max(8, dims.w - TIPW - 8));
    tip = { top, left };
  } else {
    tip = { top: Math.max(8, dims.h - TIPH - 16), left: Math.max(8, dims.w - TIPW - 16) };
  }

  const learnerName = learner
    ? (`${learner.first_name || ''} ${learner.last_name || ''}`.trim() || learner.email)
    : '';
  const signInHref = companySlug ? `/${companySlug}/passwordless_sign_in` : '#';
  const stepHeadline = current.title || current.name || '';

  return (
    <div style={{ width: '100%', maxWidth: 1160, margin: '0 auto', background: '#fff', border: '1px solid #D7DFE3', borderRadius: 14, overflow: 'hidden' }}>
      {showNav && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 56, padding: '0 22px', background: navBg, borderBottom: '1px solid rgba(0,0,0,.06)' }}>
          <div>
            {logo
              ? <img src={logo} alt={brandName || 'Logo'} style={{ height: 28, maxWidth: 180, objectFit: 'contain', display: 'block' }} />
              : <span style={{ fontWeight: 700, fontSize: 16, color: navText }}>{brandName}</span>}
          </div>
          <div>
            {learnerName
              ? <span style={{ fontWeight: 600, fontSize: 14, color: navText }}>{learnerName}</span>
              : <a href={signInHref} style={{ fontWeight: 600, fontSize: 14, color: navLink, textDecoration: 'none' }}>Magic Link Learner Sign In</a>}
          </div>
        </div>
      )}

      <div ref={stageRef} style={{ position: 'relative', overflow: 'hidden', background: '#F1F4F6' }}>
        {current.backgroundUrl
          ? <img src={current.backgroundUrl} alt="" style={{ display: 'block', width: '100%' }} />
          : <div style={{ height: 440 }} />}

        {done ? (
          <React.Fragment>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(12,22,30,.72)' }} />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 330, background: '#fff', borderRadius: 14, boxShadow: '0 18px 40px rgba(0,0,0,.35)', textAlign: 'center', padding: '28px 26px' }}>
                <div style={{ width: 64, height: 64, borderRadius: '50%', background: stepColor, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                </div>
                <div style={{ fontWeight: 800, fontSize: 20, color: hlColor }}>You're all done</div>
                <div style={{ fontSize: 13.5, color: descColor, margin: '6px 0 4px', lineHeight: 1.5 }}>You've completed {title || 'this walkthrough'}.</div>
                <div style={{ fontSize: 12, color: '#93A1AC', marginBottom: 20 }}>{list.length} of {list.length} steps completed</div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                  <button type="button" onClick={restart} style={{ border: '1px solid #D7DFE3', background: '#fff', borderRadius: 8, padding: '9px 16px', fontWeight: 600, fontSize: 13, color: '#5A6B7E', cursor: 'pointer', fontFamily: 'inherit' }}>&#8635; Restart</button>
                  <button type="button" onClick={exitToHub} style={{ border: 0, borderRadius: 8, padding: '9px 20px', background: buttonColor, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Exit</button>
                </div>
              </div>
            </div>
          </React.Fragment>
        ) : (
        <React.Fragment>
        {spot ? (
          <div
            onClick={hotspotAdvances ? () => handleForward() : undefined}
            style={{
              position: 'absolute', left: spot.left, top: spot.top, width: spot.w, height: spot.h,
              borderRadius: 8, boxShadow: `0 0 0 9999px rgba(12,22,30,.72), 0 0 0 3px ${ring}`,
              cursor: hotspotAdvances ? 'pointer' : 'default'
            }}
          />
        ) : (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(12,22,30,.72)' }} />
        )}

        <div style={{ position: 'absolute', top: tip.top, left: tip.left, width: TIPW, background: '#fff', borderRadius: 12, boxShadow: '0 18px 40px rgba(0,0,0,.35)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px 4px' }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#93A1AC' }}>
              Step {index + 1} of {list.length}
            </span>
            {stepHeadline ? <div style={{ fontWeight: 800, fontSize: 15, marginTop: 6, color: hlColor, fontFamily: hlFont ? `${hlFont}, sans-serif` : undefined }}>{stepHeadline}</div> : null}
            {current.description ? (
              <div
                style={{ fontSize: 13, lineHeight: 1.5, marginTop: 5, color: descColor, fontFamily: descFont ? `${descFont}, sans-serif` : undefined }}
                dangerouslySetInnerHTML={{ __html: richHtml(current.description) }}
              />
            ) : null}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px 14px' }}>
            <span style={{ display: 'flex', gap: 6 }}>
              {list.map((s, k) => (
                <span key={s.id || k} style={{ width: k === index ? 18 : 7, height: 7, borderRadius: 4, background: k === index ? stepColor : '#D7DFE3', display: 'inline-block' }} />
              ))}
            </span>
            <span style={{ display: 'flex', gap: 8 }}>
              {!isFirst && (
                <button type="button" onClick={onPrev} style={{ border: '1px solid #D7DFE3', background: '#fff', borderRadius: 8, padding: '7px 12px', fontWeight: 600, fontSize: 12.5, color: '#5A6B7E', cursor: 'pointer', fontFamily: 'inherit' }}>Back</button>
              )}
              {showNext && (
                <button type="button" onClick={handleForward} style={{ border: 0, borderRadius: 8, padding: '7px 14px', background: buttonColor, color: '#fff', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit' }}>
                  {isLast ? 'Finish' : 'Next'}
                </button>
              )}
            </span>
          </div>
        </div>
        </React.Fragment>
        )}
      </div>
    </div>
  );
};

export default WalkthroughSpotlight;
